import { z } from 'zod';
import { bcp47, timestamp, uuid } from './common.js';
import { listQueryInput, paginated } from './list.js';
import { previewDescriptor } from './preview.js';
import { documentKind, difficultyLevel } from './document.js';
/**
 * Discovery (§4, F8/F30/F31). `categories`, `item_categories`,
 * `search_documents`, `suppressions`.
 */
export const categorySchema = z.object({
    id: uuid,
    slug: z.string().min(1).max(80), // UNIQUE
    /** Display name is a catalog key (localized), never a stored English string. */
    nameKey: z.string().min(1),
    parentId: uuid.nullable(),
    isSeed: z.boolean(),
    sortOrder: z.number().int(),
    createdAt: timestamp,
});
/**
 * `category.list` result — the FULL category catalog (F31 filter facet, §5.9).
 * Unlike `document.list`/`file.list`/`search.query`, `categories` is a small,
 * admin-curated taxonomy (same class as `credit_packs`/`action_credit_costs`
 * — E1's "template & persona catalogs" cache entry), not a per-user growing
 * table, so it's a plain cached catalog read rather than a `listQueryInput`
 * page. THE source for `CategoryFilterChips` — never derived client-side
 * from the currently-loaded `search.query` result page (that facet
 * self-eliminates: selecting a category narrows `rows`, which then narrows
 * the chip set built from those same `rows`).
 */
export const categoryListResult = z.object({ categories: z.array(categorySchema) });
/** How a document was tagged (F31 auto-categorize vs manual). */
export const categorySource = z.enum(['auto', 'manual']);
export const itemCategorySchema = z.object({
    documentId: uuid,
    categoryId: uuid,
    source: categorySource,
    createdAt: timestamp,
});
/**
 * `search_documents` — FTS row maintained by the `search_index` worker. The
 * `tsv tsvector` + GIN index are DB-internal and not represented here (not
 * JSON-serializable); this is the readable projection.
 */
export const searchDocumentSchema = z.object({
    shareId: uuid,
    ownerId: uuid,
    documentId: uuid,
    title: z.string(),
    snippet: z.string(), // denormalized
    language: bcp47,
    updatedAt: timestamp,
});
/** Per-user "never show again" anti-join at query time (F30) — never global. */
export const suppressionSchema = z.object({
    id: uuid,
    userId: uuid,
    shareId: uuid, // UNIQUE (userId, shareId)
    createdAt: timestamp,
});
/** `suppression.add`. */
export const suppressionAddInput = z.object({ shareId: uuid });
/** `suppression.remove` — the F30 "Hidden — Undo" snackbar's undo action. */
export const suppressionRemoveInput = z.object({ shareId: uuid });
export const suppressionRemoveResult = z.object({ shareId: uuid, removed: z.boolean() });
// --- search (F8) ---------------------------------------------------------------
//
// `search_documents` indexes ONLY approved-public shares (CODE_GUIDELINES
// §15.3: a `link` share is never indexed; `private`/`user` shares aren't
// discoverable at all) — `search.query` is the DISCOVERY half of the top-bar
// overlay's three groups. "My documents" and "Templates" are separate
// existing owner-scoped list ops (`document.list`, a future `template.list`),
// not this op — the overlay composes all three client-side.
export const searchSortColumns = ['relevance', 'updatedAt'];
/** `search.query` — FTS over `search_documents` + F31 category/difficulty filters. */
export const searchQuery = listQueryInput(searchSortColumns, {
    defaultSort: 'relevance',
    defaultDir: 'desc',
}).extend({
    kind: documentKind.optional(),
    categoryId: uuid.optional(),
    language: bcp47.optional(),
    difficultyLevel: difficultyLevel.optional(),
});
/** One `search.query` result row — snippet carries `<mark>` highlight spans (F8). */
export const searchResultRow = z.object({
    shareId: uuid,
    documentId: uuid,
    ownerId: uuid,
    title: z.string(),
    /** Denormalized + `ts_headline`-highlighted (`<mark>…</mark>`), never raw HTML from user input beyond that. */
    snippetHtml: z.string(),
    language: bcp47,
    kind: documentKind,
    difficultyLevel,
    categories: z.array(z.object({ id: uuid, slug: z.string(), nameKey: z.string() })),
    preview: previewDescriptor.nullable(),
    publishedAt: timestamp.nullable(),
});
export const searchQueryResult = paginated(searchResultRow);
/**
 * Live push on `search:{userId}` (§5.5) — "signal, not payload"; the client
 * refetches the current `search.query` page on receipt. Covers both halves
 * of F8's live-refresh AC: a reindex (new/updated public share) and a
 * suppression change (F30) removing/restoring a result for THIS user.
 */
export const searchIndexPush = z.object({
    event: z.enum(['reindexed', 'suppressed', 'unsuppressed', 'category_assigned']),
    /** Null for a bulk/category-catalog event with no single subject share. */
    shareId: uuid.nullable(),
});
